import { ActionIcon, Badge, Group, Text } from "@mantine/core";
import { IconEdit } from "@tabler/icons-react";
import { useAppSelector } from "app/hooks";
import {
  EmailAppointmentNotificationTemplate,
  OrgPermissions,
} from "beitary-shared";
import {
  ArchiveConfirmationModal,
  BDataTable,
  DearchiveConfirmationModal,
  DeleteConfirmationModal,
} from "components";
import { selectEmailAppointmentNotificationTemplates } from "features/admin/client-communication/ClientCommunication.slice";
import { selectAppointmentTypes } from "features/admin/schedule-settings/ScheduleSettings.slice";
import { useDBServices } from "hooks/useDBService/useDBService";
import { BCan } from "permissions";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { BDataTableColumn } from "schemas-types";

type EmailAppointmentNotificationTemplatesTableItem =
  EmailAppointmentNotificationTemplate & {
    aptTpes: string;
    actions?: undefined;
  };

export const EmailAppointmentNotificationTemplatesTable = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const emailAppointmentNotificationTemplates = useAppSelector(
    selectEmailAppointmentNotificationTemplates
  );
  const appointmentTypes = useAppSelector(selectAppointmentTypes);

  const tableItems: EmailAppointmentNotificationTemplatesTableItem[] =
    emailAppointmentNotificationTemplates.map(
      (emailAppointmentNotificationTemplate) => ({
        ...emailAppointmentNotificationTemplate,
        aptTpes: appointmentTypes
          .filter((at) =>
            emailAppointmentNotificationTemplate.aptTypeIds.includes(at.id)
          )
          .map((at) => at.name)
          .join("[-]"),
      })
    );

  const {
    deleteEmailAppointmentNotificationTemplate,
    archiveEmailAppointmentNotificationTemplate,
    dearchiveEmailAppointmentNotificationTemplate,
  } =
    useDBServices().clientCommunication.emailAppointmentNotificationsTemplates;

  const columns: BDataTableColumn<EmailAppointmentNotificationTemplatesTableItem>[] =
    [
      {
        accessor: "name",
        title: <Text>{t("NAME")}</Text>,
        sortable: true,
      },
      {
        accessor: "type",
        title: <Text>{t("TYPE")}</Text>,
        sortable: true,
        render: ({ type }) => <Text>{t(type)}</Text>,
      },
      {
        accessor: "aptTpes",
        title: <Text>{t("APPOINTMENT_TYPES")}</Text>,
        sortable: true,
        render: ({ aptTpes }) => (
          <Group>
            {aptTpes.split("[-]").map((a) => (
              <Badge>{a}</Badge>
            ))}
          </Group>
        ),
      },
      {
        accessor: "status",
        title: <Text>{t("STATUS")}</Text>,
        sortable: true,
        render: ({ status }) => (
          <Text color={status === "ACTIVE" ? "dark" : "dimmed"}>
            {t(status)}
          </Text>
        ),
      },
      {
        accessor: "actions",
        title: "",
        width: 140,
        render: (emailAppointmentNotificationTemplate) => (
          <BCan I="do" a={OrgPermissions.CLIENT_COMMUNICATION}>
            <Group position="right">
              <ActionIcon
                onClick={() =>
                  navigate(`edit/${emailAppointmentNotificationTemplate.id}`)
                }
              >
                <IconEdit size={18} />
              </ActionIcon>
              {emailAppointmentNotificationTemplate.status === "ARCHIVED" ? (
                <DearchiveConfirmationModal
                  f={async () =>
                    dearchiveEmailAppointmentNotificationTemplate(
                      emailAppointmentNotificationTemplate.id
                    )
                  }
                  item={emailAppointmentNotificationTemplate}
                />
              ) : (
                <ArchiveConfirmationModal
                  f={async () =>
                    archiveEmailAppointmentNotificationTemplate(
                      emailAppointmentNotificationTemplate.id
                    )
                  }
                  item={emailAppointmentNotificationTemplate}
                />
              )}
              <DeleteConfirmationModal
                f={async () =>
                  deleteEmailAppointmentNotificationTemplate(
                    emailAppointmentNotificationTemplate.id
                  )
                }
                item={emailAppointmentNotificationTemplate}
              />
            </Group>
          </BCan>
        ),
      },
    ];

  return (
    <BDataTable data={tableItems} columns={columns} defaultSortKey={"name"} />
  );
};
